import { useEffect, useMemo } from 'react';
import { FiAlertTriangle, FiCalendar, FiCheckCircle, FiCreditCard } from 'react-icons/fi';
import { Account } from '../types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAccounts } from '../store/slices/accountsSlice';
import { useNavigation } from '../hooks/useNavigation';
import { useRouteScrollContainer } from '../hooks/useRouteScrollContainer';
import { formatCurrency } from '../utils/formatters';

const parseLocalDate = (date?: string | null) => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const daysUntil = (date?: string | null) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((parsed.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

const formatShortDate = (date?: string | null) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return 'No date';
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const getBillTone = (account: Account) => {
  const bill = account.creditCardBill;
  const dueInDays = daysUntil(bill?.dueDate);
  if (!bill) return 'empty';
  if (bill.status === 'OVERDUE' || (bill.status !== 'PAID' && dueInDays != null && dueInDays < 0)) return 'overdue';
  if (bill.status !== 'PAID' && dueInDays != null && dueInDays <= 1) return 'due';
  if (bill.status === 'PAID') return 'paid';
  return 'normal';
};

const getBillSubtitle = (account: Account) => {
  const bill = account.creditCardBill;
  if (!bill) return 'No bill generated yet';

  const dueInDays = daysUntil(bill.dueDate);
  if (bill.status === 'PAID') {
    return bill.paidOnDate ? `Paid on ${formatShortDate(bill.paidOnDate)}` : 'Bill paid';
  }
  if (dueInDays == null) {
    return `Due ${formatShortDate(bill.dueDate)}`;
  }
  if (dueInDays < 0) {
    return `${Math.abs(dueInDays)} day${Math.abs(dueInDays) === 1 ? '' : 's'} overdue`;
  }
  if (dueInDays === 0) {
    return 'Due today';
  }
  return `Due in ${dueInDays} day${dueInDays === 1 ? '' : 's'}`;
};

function BillsScreen() {
  const dispatch = useAppDispatch();
  const { navigateTo } = useNavigation();
  const setRouteScrollContainer = useRouteScrollContainer<HTMLDivElement>('screen:bills');
  const { accounts, status, error } = useAppSelector((state) => state.accounts);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchAccounts());
    }
  }, [dispatch, status]);

  const creditCards = useMemo(
    () => accounts
      .filter((account) => account.type === 'CREDIT_CARD')
      .sort((left, right) => {
        const leftTone = getBillTone(left);
        const rightTone = getBillTone(right);
        const priority = { overdue: 4, due: 3, normal: 2, empty: 1, paid: 0 };
        return priority[rightTone] - priority[leftTone] || left.name.localeCompare(right.name);
      }),
    [accounts]
  );

  const totalDue = creditCards.reduce((sum, account) => {
    const bill = account.creditCardBill;
    if (!bill || bill.status === 'PAID') return sum;
    return sum + Math.max(0, bill.billAmount - bill.paidAmount);
  }, 0);

  return (
    <div ref={setRouteScrollContainer} className="relative h-full overflow-y-auto bg-background text-foreground enable-scroll">
      <div className="mx-auto flex min-h-full w-full max-w-lg flex-col pb-28">
        <header className="flex flex-shrink-0 items-center justify-between gap-4 px-5 py-2.5">
          <h1 className="text-3xl font-bold tracking-normal">Bills</h1>
          <div className="text-right">
            <div className="text-base font-semibold">{formatCurrency(totalDue)}</div>
            <div className="text-xs font-medium text-muted-foreground">Total due</div>
          </div>
        </header>

        {status === 'loading' && accounts.length === 0 && (
          <div className="mx-4 mt-2 rounded-2xl border border-border bg-card p-6 text-center text-muted-foreground">
            Loading bills...
          </div>
        )}

        {status === 'failed' && (
          <div className="mx-4 mt-2 rounded-2xl border border-border bg-card p-6 text-center text-error">
            {error}
          </div>
        )}

        {status === 'succeeded' && creditCards.length === 0 && (
          <div className="mx-4 mt-2 rounded-2xl border border-border bg-card p-6 text-center text-muted-foreground">
            No credit card bills found.
          </div>
        )}

        <div className="space-y-3 px-4 pt-2">
          {creditCards.map((account) => {
            const bill = account.creditCardBill;
            const pendingAmount = bill ? Math.max(0, bill.billAmount - bill.paidAmount) : 0;
            const tone = getBillTone(account);
            const Icon = tone === 'paid' ? FiCheckCircle : tone === 'overdue' || tone === 'due' ? FiAlertTriangle : FiCalendar;
            const toneClass = tone === 'paid'
              ? 'bg-success/15 text-success'
              : tone === 'overdue' || tone === 'due'
                ? 'bg-error/15 text-error'
                : 'bg-muted/40 text-muted-foreground';

            return (
              <button
                type="button"
                key={account.id}
                onClick={() => navigateTo(`/accounts/${account.id}/credit-card-bills`, { state: { account } })}
                className="w-full rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-xl p-3 ${toneClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-base font-semibold">{account.name}</div>
                        <div className="mt-1 text-sm text-muted-foreground">{getBillSubtitle(account)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(pendingAmount)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {bill?.dueDate ? formatShortDate(bill.dueDate) : 'No due'}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                      <FiCreditCard className="h-4 w-4" />
                      <span className="truncate">{account.accountNumber ? `**${account.accountNumber.slice(-4)}` : account.currency}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default BillsScreen;
